const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_BASE_URL;

// ── 画像前処理（クライアント側） ──────────────────────────────────────

/**
 * Canvas API を使って画像を横幅最大 maxWidth にリサイズし、Base64文字列を返す
 * （Storage への直接アップロードは行わない。Functions に渡すだけ）
 *
 * @param {File} file
 * @param {number} maxWidth
 * @param {number} quality
 * @returns {Promise<string>} Base64 DataURL（"data:image/jpeg;base64,..."）
 */
const resizeImageToBase64 = (file, maxWidth, quality) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxWidth / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.src = url;
  });

// ── 投稿作成 ────────────────────────────────────────────────────────

/**
 * 足跡を投稿する
 * ① 写真をリサイズして Base64 に変換（クライアント側）
 * ② 写真・手書き画像の Base64 データを Functions に POST
 * ③ Functions 側で Storage 保存 → Firestore 保存（トランザクション管理はサーバー側）
 *
 * @param {{ photoFile: File, handwritingDataUrl: string, nickname: string, isSuper: boolean, location: {lat: number, lng: number} }} params
 * @returns {Promise<{ success: boolean, postId?: string, code?: string, message?: string }>}
 */
export const createFootprint = async ({ photoFile, handwritingDataUrl, nickname, isSuper, location, address }) => {
  // 写真をリサイズして Base64 化（Storage には送らない）
  const photoData = await resizeImageToBase64(photoFile, 1200, 0.8);

  const res = await fetch(`${FUNCTIONS_BASE_URL}/createFootprint`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      nickname,
      photoData,
      handwritingData: handwritingDataUrl,
      isSuper,
      location,
      address: address ?? null,
    }),
  });

  return res.json();
};

// ── 一覧取得 ─────────────────────────────────────────────────────────

/**
 * 足跡一覧を取得する（Functions経由）
 * 消滅判定はクライアント側で行う
 * ※ photoUrl / handwritingUrl は含まれない（詳細はfetchFootprintDetailで取得）
 *
 * @param {{ limit?: number, cursor?: string | null }} options
 */
export const fetchFootprints = async ({ limit = 100, cursor = null } = {}) => {
  const params = new URLSearchParams({ limit });
  if (cursor) params.append('cursor', cursor);
  const res = await fetch(`${FUNCTIONS_BASE_URL}/getFootprints?${params}`);
  return res.json();
};

// ── 詳細取得（ボトムシート表示時のみ呼ぶ） ──────────────────────────────

/**
 * 足跡の詳細（photoUrl・handwritingUrl）を取得する
 * Functions API 経由。localStorage に24時間キャッシュする。
 *
 * キャッシュ方針:
 *   - 足跡投稿は不変（編集不可）のため、キャッシュの鮮度は問題ない
 *   - 複数タブ・ページリロード後でもキャッシュが利用されるため Firestore リード削減に効果的
 *   - 削除済み投稿は getFootprintDetail が404を返すため、古いキャッシュは自然に無視される
 *   - TTL: 24時間（足跡の表示期限 48h/72h より短い）
 *
 * @param {string} postId
 * @returns {Promise<{ photoUrl: string, handwritingUrl: string }>}
 */
export const fetchFootprintDetail = async (postId) => {
  const cacheKey = `footprint_detail_${postId}`;

  // localStorage からキャッシュ確認（24時間以内なら使用）
  const cached = localStorage.getItem(cacheKey);
  if (cached) {
    try {
      const { data, timestamp } = JSON.parse(cached);
      if (Date.now() - timestamp < 24 * 60 * 60 * 1000) return data;
    } catch {
      localStorage.removeItem(cacheKey);
    }
  }

  // Functions API 経由で取得
  const res = await fetch(`${FUNCTIONS_BASE_URL}/getFootprintDetail?postId=${postId}`);
  const data = await res.json();
  if (!data.success) throw new Error(data.message ?? '投稿が見つかりません');

  const detail = { photoUrl: data.photoUrl, handwritingUrl: data.handwritingUrl };

  localStorage.setItem(cacheKey, JSON.stringify({ data: detail, timestamp: Date.now() }));

  return detail;
};

// ── 「の」リアクション ───────────────────────────────────────────────

/**
 * 「の」リアクションを1件加算する（Functions API 経由）
 * スパムチェックはサーバー側（req.ip + postId + JST日付）で実施
 *
 * @param {string} postId
 * @returns {Promise<{ success: boolean, code?: string, message?: string }>}
 */
export const addReaction = async (postId) => {
  const res = await fetch(`${FUNCTIONS_BASE_URL}/addReaction`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ postId }),
  });
  return res.json();
};

// ── 管理者操作（Functions API 経由） ─────────────────────────────────

/**
 * 管理者用リクエストヘルパー
 * Firebase Auth のIDトークンをAuthorizationヘッダーに付与する
 */
const adminFetch = async (path, options = {}) => {
  const { getAuth } = await import('firebase/auth');
  const token = await getAuth().currentUser?.getIdToken();
  return fetch(`${FUNCTIONS_BASE_URL}/${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers ?? {}),
    },
  });
};

export const hideFootprint = async (postId, isVisible) => {
  const res = await adminFetch('hideFootprint', {
    method: 'POST',
    body: JSON.stringify({ postId, isVisible }),
  });
  return res.json();
};

export const deleteFootprint = async (postId) => {
  const res = await adminFetch('deleteFootprint', {
    method: 'POST',
    body: JSON.stringify({ postId }),
  });
  return res.json();
};

export const updateDailyLimit = async (newLimit) => {
  const res = await adminFetch('updateDailyLimit', {
    method: 'POST',
    body: JSON.stringify({ dailyLimit: newLimit }),
  });
  return res.json();
};

export const fetchAllFootprintsForAdmin = async ({ cursor = null } = {}) => {
  const params = new URLSearchParams();
  if (cursor) params.append('cursor', cursor);
  const res = await adminFetch(`fetchAllFootprintsForAdmin?${params}`);
  const data = await res.json();
  return {
    posts: data.posts ?? [],
    hasMore: data.hasMore ?? false,
    nextCursor: data.nextCursor ?? null,
  };
};

export const fetchDailyLimit = async () => {
  const res = await adminFetch('fetchDailyLimit');
  const data = await res.json();
  return data.dailyLimit ?? 1000;
};
